import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    setMics,
    setSpeakers,
    setCameras,
    setSelectedMic,
    setSelectedSpeaker,
    setSelectedCamera,
} from '../features/MediaControls/store/mediaControlSlice';
import {
    toggleLivekitMic,
    toggleLivekitCamera,
} from "../features/MediaPlayback/store/livekitSlice/livekitSlice"


//Helper functions and module level states
const initialDeviceListState = { mics: [], speakers: [], cameras: [] };
let previousDeviceLists = { ...initialDeviceListState };
let previousDefaultDevices = { mic: null, speaker: null, camera: null };
const IS_MEDIA_DEVICES_SUPPORTED = !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices);

const isRealDevice = (d) => {
    if (!d.label) return true;              // no label → accept it (Firefox pre-permission)
    return !d.label.toLowerCase().includes("monitor");  // label exists → must NOT contain "monitor"
  };
  

const createDeviceObject = (device) => {
    // Fallback for deviceId and label if they are not present
    const deviceId = device.deviceId || 'default';
    // If label is missing, create a generic one. Especially useful before permissions grant.
    const label =
        device.label ||
        ` ${deviceId === 'default' ? 'Default' : deviceId.slice(0, 8)}`;
    return {
        deviceId,
        label,
        kind: device.kind,
        groupId: device.groupId,
        // Heuristic for identifying default devices. '' deviceId is common for system default placeholders.
        // The 'isDefault' property is non-standard but included if present.
        isDefault: device.deviceId === '' || device.deviceId === 'default' || (device.isDefault === true),
    };
};


const listsHaveChanged = (prevList, currentList) => {
    // FIX: Use prevList.length instead of previousDeviceLists.length
    if (prevList.length !== currentList.length) return true;
    const prevIds = prevList.map((d) => d.deviceId).sort();
    const currentIds = currentList.map((d) => d.deviceId).sort();
    return !prevIds.every((id, index) => id === currentIds[index]);
};

const findDeviceById = (devices, deviceId) => {
    return devices.find((device) => device.deviceId === deviceId) || null
};

async function fetchCurrentDeviceData() {
    if (!IS_MEDIA_DEVICES_SUPPORTED) {
        console.warn('[DeviceListener] MediaDevices API not supported.');
        return { mics: [], speakers: [], cameras: [], defaultMic: null, defaultSpeaker: null, defaultCamera: null };
    } 
    const isFirefox = typeof InstallTrigger !== "undefined";
    if(isFirefox) {
        try {
            // This may fail if user denies camera or audio
           const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
           stream.getTracks().forEach(track => track.stop());
        } catch (err) {
            console.warn("Permissions denied, continuing anyway.", err);
        }
    }

    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const currentMics = devices.filter(d => d.kind === 'audioinput' && isRealDevice(d)).map(createDeviceObject);
        const currentSpeakers = devices.filter(d => d.kind === 'audiooutput' && isRealDevice(d)).map(createDeviceObject);
        const currentCameras = devices.filter(d => d.kind === 'videoinput' && isRealDevice(d)).map(createDeviceObject);

        const findDefault = (list) => list.find(d => d.isDefault || d.deviceId === 'default') || list[0] || null;
        return {
            mics: currentMics,
            speakers: currentSpeakers,
            cameras: currentCameras,
            defaultMic: findDefault(currentMics),
            defaultSpeaker: findDefault(currentSpeakers),
            defaultCamera: findDefault(currentCameras),
        };
    } catch (error) {
        console.error('[DeviceListener] Error enumerating devices:', error);
        return { mics: [], speakers: [], cameras: [], defaultMic: null, defaultSpeaker: null, defaultCamera: null };
    }
}



export const refreshDevices = createAsyncThunk(
    'global/refreshDevices',
    async (_, { dispatch, getState }) => {

        const currentDevicesData = await fetchCurrentDeviceData();
        const {
            mics: newMics, speakers: newSpeakers, cameras: newCameras,
            defaultMic: newDefaultMic, defaultSpeaker: newDefaultSpeaker, defaultCamera: newDefaultCamera
        } = currentDevicesData;

        // Get current selections and active states from Redux store
        const {
            selectedMic, selectedSpeaker, selectedCamera} = getState().mediaControls;

        // 1. Update device lists only if they actually changed
        const micsChanged = listsHaveChanged(previousDeviceLists.mics, newMics);
        const speakersChanged = listsHaveChanged(previousDeviceLists.speakers, newSpeakers);
        const camerasChanged = listsHaveChanged(previousDeviceLists.cameras, newCameras);

        if (micsChanged) {
            dispatch(setMics(newMics));
        }
        if (speakersChanged) {
            dispatch(setSpeakers(newSpeakers));
        }
        if (camerasChanged) {
            dispatch(setCameras(newCameras));
        }

        // 2. Handle device selection changes ONLY when necessary
        
        // Auto-select for Mics - only if no mic is selected OR selected mic was removed OR default changed
        if ((!selectedMic || selectedMic?.deviceId === 'default') && newMics.length > 0) {
            // Initial selection - no mic selected yet
            dispatch(setSelectedMic(newMics[0]));
        } else if (selectedMic && !findDeviceById(newMics, selectedMic.deviceId)) {
            // Selected mic was removed - turn off and select new default
            dispatch(toggleLivekitMic(false));
            dispatch(setSelectedMic(newDefaultMic || (newMics.length > 0 ? newMics[0] : null)));
        } else if (
            previousDefaultDevices.mic && 
            newDefaultMic &&
            previousDefaultDevices.mic.deviceId !== newDefaultMic.deviceId &&
            selectedMic && 
            selectedMic.deviceId === previousDefaultDevices.mic.deviceId
        ) {
            // Default device changed and user was using the old default - switch to new default
            dispatch(setSelectedMic(newDefaultMic));
        }

        // Auto-select for Cameras - only if no camera is selected OR selected camera was removed OR default changed
        if ((!selectedCamera || selectedCamera?.deviceId === 'default') && newCameras.length > 0) {
            // Initial selection - no camera selected yet
            dispatch(setSelectedCamera(newCameras[0]));
        } 
        else if (selectedCamera && !findDeviceById(newCameras, selectedCamera.deviceId) 
            && selectedCamera.deviceId !== 'default' && selectedCamera.deviceId !== '') {
            // Selected camera was removed, but was not the default - turn off and select new default
            dispatch(toggleLivekitCamera(false));
            dispatch(setSelectedCamera(newDefaultCamera || (newCameras.length > 0 ? newCameras[0] : null)));
        }

        // Auto select for Speakers - only if no speaker is selected OR selected speaker was removed OR default changed
        if ((!selectedSpeaker || selectedSpeaker?.deviceId === 'default') && newSpeakers.length > 0) {
            // Initial selection - no speaker selected yet
            dispatch(setSelectedSpeaker(newSpeakers[0]));
        } else if (selectedSpeaker && !findDeviceById(newSpeakers, selectedSpeaker.deviceId)) {
            // Selected speaker was removed - select new default (no need to toggle off for speakers)
            dispatch(setSelectedSpeaker(newDefaultSpeaker || (newSpeakers.length > 0 ? newSpeakers[0] : null)));
        } else if (
            previousDefaultDevices.speaker && 
            newDefaultSpeaker &&
            previousDefaultDevices.speaker.deviceId !== newDefaultSpeaker.deviceId &&
            selectedSpeaker && 
            selectedSpeaker.deviceId === previousDefaultDevices.speaker.deviceId
        ) {
            // Default device changed and user was using the old default - switch to new default
            dispatch(setSelectedSpeaker(newDefaultSpeaker));
        }

        // Update module-level "previous" state trackers
        previousDeviceLists = { mics: newMics, speakers: newSpeakers, cameras: newCameras };
        previousDefaultDevices = { mic: newDefaultMic, speaker: newDefaultSpeaker, camera: newDefaultCamera };

    }
    
);